import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import axios from "../utils/axiosConfig";
import { API_BASE_URL } from "../utils/apiConfig";
import toast from "react-hot-toast";

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within ChatProvider");
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [typingUsers, setTypingUsers] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const [joinLeaveMessages, setJoinLeaveMessages] = useState([]);
  const messagesEndRef = useRef(null);
  const notificationSound = useRef(null);
  const messageIdsSet = useRef(new Set());
  const isSocketConnected = useRef(false);

  // Load notification sound
  useEffect(() => {
    notificationSound.current = new Audio();
    notificationSound.current.src =
      "data:audio/wav;base64,U3RlYWx0aCBzb3VuZA==";
  }, []);

  // Connect to socket when authenticated
  useEffect(() => {
    if (isAuthenticated && user && !socket) {
      connectSocket();
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [isAuthenticated, user]);

  const connectSocket = () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    // Prevent multiple connections
    if (isSocketConnected.current) return;
    isSocketConnected.current = true;

    const newSocket = io(API_BASE_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // Store event listeners to prevent duplicates
    const eventListeners = new Map();

    const addUniqueListener = (event, handler) => {
      if (!eventListeners.has(event)) {
        eventListeners.set(event, handler);
        newSocket.on(event, handler);
      }
    };

    newSocket.on("connect", () => {
      setIsConnected(true);
      setError(null);
    });

    newSocket.on("disconnect", () => {
      setIsConnected(false);
      isSocketConnected.current = false;
    });

    newSocket.on("connect_error", (err) => {
      console.error("Socket connection error:", err);
      setError("Failed to connect to chat server");
      setIsConnected(false);
    });

    // Handle chat history
    addUniqueListener("chat_history", (history) => {
      messageIdsSet.current.clear();
      history.forEach((msg) => {
        if (msg._id) messageIdsSet.current.add(msg._id);
      });
      setMessages(history);
      setIsLoading(false);
    });

    // Handle new messages
    addUniqueListener("new_message", (message) => {
      if (message._id && !messageIdsSet.current.has(message._id)) {
        messageIdsSet.current.add(message._id);
        setMessages((prev) => [...prev, message]);

        // Only show toast notification if chat is not open
        if (
          !isChatOpen &&
          message.sender?._id !== user?.id &&
          message.sender?._id !== user?._id
        ) {
          setUnreadCount((prev) => prev + 1);
          if (notificationSound.current) {
            notificationSound.current.play().catch(() => {});
          }

          toast.custom(
            (t) => (
              <div
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  padding: "10px 14px",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                  fontSize: "12px",
                  boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                }}
                onClick={() => {
                  toast.dismiss(t.id);
                  openChat();
                }}
              >
                <span style={{ fontSize: "16px" }}>💬</span>
                <div>
                  <strong>{message.senderName}</strong>
                  <p style={{ margin: 0, fontSize: "10px", opacity: 0.9 }}>
                    {message.message.substring(0, 40)}
                  </p>
                </div>
              </div>
            ),
            { duration: 3000 },
          );
        }
      }
    });

    // Handle user joined
    addUniqueListener("user_joined", (data) => {
      setJoinLeaveMessages((prev) => [
        ...prev,
        {
          type: "join",
          name: data.name,
          message: data.message,
          timestamp: new Date(),
        },
      ]);
      setTimeout(() => {
        setJoinLeaveMessages((prev) => prev.filter((m) => m !== data));
      }, 5000);
      fetchActiveUsers();
    });

    // Handle user left
    addUniqueListener("user_left", (data) => {
      setJoinLeaveMessages((prev) => [
        ...prev,
        {
          type: "leave",
          name: data.name,
          message: data.message,
          timestamp: new Date(),
        },
      ]);
      setTimeout(() => {
        setJoinLeaveMessages((prev) => prev.filter((m) => m !== data));
      }, 5000);
      fetchActiveUsers();
    });

    // Handle user status change
    addUniqueListener("user_status", (data) => {
      setActiveUsers((prev) => {
        const exists = prev.find((u) => u.userId === data.userId);
        if (data.isOnline && !exists) {
          return [
            ...prev,
            { userId: data.userId, name: data.name, role: data.role },
          ];
        } else if (!data.isOnline && exists) {
          return prev.filter((u) => u.userId !== data.userId);
        }
        return prev;
      });
    });

    // Handle active users count
    addUniqueListener("active_users_count", (count) => {
      setActiveUsersCount(count);
    });

    // Handle typing indicator
    addUniqueListener("user_typing", (data) => {
      if (data.isTyping) {
        setTypingUsers((prev) => ({ ...prev, [data.userId]: data.name }));
        setTimeout(() => {
          setTypingUsers((prev) => {
            const newState = { ...prev };
            delete newState[data.userId];
            return newState;
          });
        }, 2000);
      } else {
        setTypingUsers((prev) => {
          const newState = { ...prev };
          delete newState[data.userId];
          return newState;
        });
      }
    });

    // Handle message deleted
    addUniqueListener("message_deleted", (data) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === data.messageId ? { ...msg, isDeleted: true } : msg,
        ),
      );
    });

    // Handle message pinned
    addUniqueListener("message_pinned", (data) => {
      console.log(
        `Message ${data.isPinned ? "pinned" : "unpinned"} by ${data.pinnedBy}`,
      );
    });

    // Handle reaction updated
    addUniqueListener("reaction_updated", (data) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg._id === data.messageId
            ? { ...msg, reactions: data.reactions }
            : msg,
        ),
      );
    });

    // Handle message error
    addUniqueListener("message_error", (data) => {
      toast.error(data.error);
    });

    setSocket(newSocket);
    fetchActiveUsers();
  };

  const fetchActiveUsers = async () => {
    try {
      const response = await axios.get("/api/chat/active-users");
      setActiveUsers(response.data.users);
      setActiveUsersCount(response.data.count);
    } catch (error) {
      console.error("Failed to fetch active users:", error);
    }
  };

  const sendMessage = useCallback(
    (message, replyToId = null) => {
      if (socket && isConnected && message.trim()) {
        socket.emit("send_message", { message, replyToId });
        return true;
      }
      return false;
    },
    [socket, isConnected],
  );

  const sendTyping = useCallback(
    (isTyping) => {
      if (socket && isConnected) {
        socket.emit("typing", { isTyping });
      }
    },
    [socket, isConnected],
  );

  const addReaction = useCallback(
    (messageId, emoji) => {
      if (socket && isConnected) {
        socket.emit("add_reaction", { messageId, emoji });
      }
    },
    [socket, isConnected],
  );

  const deleteMessage = useCallback(
    (messageId) => {
      if (socket && isConnected) {
        socket.emit("delete_message", { messageId });
      }
    },
    [socket, isConnected],
  );

  const pinMessage = useCallback(
    (messageId) => {
      if (socket && isConnected) {
        socket.emit("pin_message", { messageId });
      }
    },
    [socket, isConnected],
  );

  const reportMessage = useCallback(async (messageId, reason, description) => {
    try {
      const response = await axios.post("/api/chat/report", {
        messageId,
        reason,
        description,
      });
      if (response.data.success) {
        toast.success("Report submitted. Our moderators will review it.");
        return true;
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit report");
      return false;
    }
  }, []);

  const openChat = useCallback(() => {
    setIsChatOpen(true);
    setUnreadCount(0);
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
  }, []);

  const toggleChat = useCallback(() => {
    setIsChatOpen((prev) => !prev);
    if (!isChatOpen) {
      setUnreadCount(0);
    }
  }, [isChatOpen]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const loadMessages = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get("/api/chat/messages/general?limit=50");
      if (response.data.success) {
        messageIdsSet.current.clear();
        response.data.messages.forEach((msg) => {
          if (msg._id) messageIdsSet.current.add(msg._id);
        });
        setMessages(response.data.messages);
      }
    } catch (error) {
      console.error("Failed to load messages:", error);
      setError("Failed to load messages");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkUserStatus = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`/api/users/${user.id || user._id}`);
      if (response.data) {
        setIsMuted(
          response.data.isMuted &&
            (!response.data.mutedUntil ||
              new Date(response.data.mutedUntil) > new Date()),
        );
        setIsBanned(
          response.data.isBanned &&
            (!response.data.bannedUntil ||
              new Date(response.data.bannedUntil) > new Date()),
        );
      }
    } catch (error) {
      console.error("Failed to check user status:", error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      checkUserStatus();
    }
  }, [user, checkUserStatus]);

  const value = {
    socket,
    isConnected,
    messages,
    activeUsers,
    activeUsersCount,
    isChatOpen,
    unreadCount,
    typingUsers,
    isLoading,
    error,
    isMuted,
    isBanned,
    joinLeaveMessages,
    messagesEndRef,
    sendMessage,
    sendTyping,
    addReaction,
    deleteMessage,
    pinMessage,
    reportMessage,
    openChat,
    closeChat,
    toggleChat,
    scrollToBottom,
    loadMessages,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
